const supportingDocsLog = (...args) => {
	console.log("[SupportingDocs]", ...args);
};

export const runSupportingDocsStandardUpload = async ({
	files,
	processingText,
	closeDrawer,
	openProcessingModal,
	setProcessingText,
	buildBeforeUpload,
	uploadFile,
	afterUpload,
	onError,
}) => {
	supportingDocsLog("runSupportingDocsStandardUpload:start", {
		filesCount: Array.isArray(files) ? files.length : 0,
		processingText,
		hasBuildBeforeUpload: typeof buildBeforeUpload === "function",
		hasUploadFile: typeof uploadFile === "function",
		hasAfterUpload: typeof afterUpload === "function",
		hasOnError: typeof onError === "function",
	});

	if (!Array.isArray(files) || files.length === 0) {
		supportingDocsLog(
			"runSupportingDocsStandardUpload: returning early because files are missing or empty",
			{
				returnValue: null,
				reason: "files array missing or empty",
			}
		);
		return null;
	}

	supportingDocsLog("runSupportingDocsStandardUpload: opening processing modal", {
		processingText,
	});
	openProcessingModal?.();
	setProcessingText?.(processingText);

	try {
		if (typeof buildBeforeUpload === "function") {
			supportingDocsLog(
				"[SupportingDocs] Calling API/Function: buildBeforeUpload",
				{ processingText }
			);
			await buildBeforeUpload();
			supportingDocsLog(
				"[SupportingDocs] API/Function response: buildBeforeUpload",
				{ returnedValue: undefined }
			);
		} else {
			supportingDocsLog(
				"runSupportingDocsStandardUpload: buildBeforeUpload skipped",
				{ reason: "buildBeforeUpload is not a function" }
			);
		}

		let lastResponse;
		for (const file of files) {
			supportingDocsLog("[SupportingDocs] Calling API/Function: uploadFile", {
				file,
			});
			lastResponse = await uploadFile(file);
			supportingDocsLog("[SupportingDocs] API/Function response: uploadFile", {
				file,
				response: lastResponse,
				responseIsNull: lastResponse === null,
				responseIsUndefined: lastResponse === undefined,
			});
		}

		if (lastResponse === null || lastResponse === undefined) {
			supportingDocsLog(
				"runSupportingDocsStandardUpload: returning early because lastResponse is missing",
				{
					lastResponse,
					reason: "uploadFile returned null/undefined",
				}
			);
			throw lastResponse ?? new Error("DOCUMENT_UPLOAD_ERROR");
		}

		if (typeof afterUpload === "function") {
			try {
				supportingDocsLog(
					"[SupportingDocs] Calling API/Function: afterUpload",
					{ lastResponse }
				);
				await afterUpload(lastResponse);
				supportingDocsLog(
					"[SupportingDocs] API/Function response: afterUpload",
					{ response: undefined }
				);
			} catch (afterUploadError) {
				supportingDocsLog(
					"runSupportingDocsStandardUpload: afterUpload failed but flow will continue",
					{
						error: afterUploadError,
					}
				);
				console.error(
					"[SupportingDocs] standard upload post-success step failed",
					afterUploadError
				);
			}
		} else {
			supportingDocsLog(
				"runSupportingDocsStandardUpload: afterUpload skipped",
				{ reason: "afterUpload is not a function" }
			);
		}

		supportingDocsLog("runSupportingDocsStandardUpload: returning success", {
			returnValue: lastResponse,
		});
		return lastResponse;
	} catch (err) {
		supportingDocsLog("runSupportingDocsStandardUpload: caught error", {
			error: err,
		});
		supportingDocsLog("runSupportingDocsStandardUpload:end", {
			completed: false,
		});
		await onError?.(err);
		supportingDocsLog("runSupportingDocsStandardUpload: returning null after error", {
			returnValue: null,
		});
		return null;
	}
};

export const runSupportingDocsEnhancedUpload = async ({
	files,
	processingText,
	closeDrawer,
	openProcessingModal,
	setProcessingText,
	getSelectedDocuments,
	getDocumentContext,
	uploadFile,
	afterDocumentUpload,
	onSuccess,
	onError,
}) => {
	supportingDocsLog("runSupportingDocsEnhancedUpload:start", {
		filesCount: Array.isArray(files) ? files.length : 0,
		processingText,
		hasGetSelectedDocuments: typeof getSelectedDocuments === "function",
		hasGetDocumentContext: typeof getDocumentContext === "function",
		hasUploadFile: typeof uploadFile === "function",
		hasAfterDocumentUpload: typeof afterDocumentUpload === "function",
		hasOnSuccess: typeof onSuccess === "function",
		hasOnError: typeof onError === "function",
	});

	if (!Array.isArray(files) || files.length === 0) {
		supportingDocsLog(
			"runSupportingDocsEnhancedUpload: returning early because files are missing or empty",
			{
				returnValue: null,
				reason: "files array missing or empty",
			}
		);
		return null;
	}

	supportingDocsLog("runSupportingDocsEnhancedUpload: opening processing modal", {
		processingText,
	});
	openProcessingModal?.();
	setProcessingText?.(processingText);

	try {
		supportingDocsLog(
			"[SupportingDocs] Calling API/Function: getSelectedDocuments",
			{ files }
		);
		const selectedDocuments = await getSelectedDocuments?.();
		supportingDocsLog(
			"[SupportingDocs] API/Function response: getSelectedDocuments",
			{
				selectedDocuments,
				isArray: Array.isArray(selectedDocuments),
				length: Array.isArray(selectedDocuments)
					? selectedDocuments.length
					: undefined,
			}
		);

		if (!Array.isArray(selectedDocuments) || selectedDocuments.length === 0) {
			supportingDocsLog(
				"runSupportingDocsEnhancedUpload: returning early because selectedDocuments is empty or invalid",
				{
					selectedDocuments,
					reason: "getSelectedDocuments returned no documents",
				}
			);
			throw new Error("Selected document not found");
		}

		for (const selectedDocument of selectedDocuments) {
			supportingDocsLog(
				"[SupportingDocs] Calling API/Function: getDocumentContext",
				{ selectedDocument }
			);
			const documentContext = await getDocumentContext(selectedDocument);
			supportingDocsLog(
				"[SupportingDocs] API/Function response: getDocumentContext",
				{
					selectedDocument,
					documentContext,
					hasDocumentContext: Boolean(documentContext),
				}
			);

			if (!documentContext) {
				supportingDocsLog(
					"runSupportingDocsEnhancedUpload: returning early because documentContext is missing",
					{
						selectedDocument,
						returnValue: null,
						reason: "getDocumentContext returned null/undefined",
					}
				);
				return null;
			}

			let lastResponse;
			for (const file of files) {
				supportingDocsLog("[SupportingDocs] Calling API/Function: uploadFile", {
					file,
					selectedDocument,
					documentContext,
				});
				lastResponse = await uploadFile({
					file,
					documentContext,
					selectedDocument,
				});
				supportingDocsLog("[SupportingDocs] API/Function response: uploadFile", {
					file,
					selectedDocument,
					documentContext,
					lastResponse,
					responseIsFalsy: !lastResponse,
				});

				if (!lastResponse) {
					supportingDocsLog(
						"runSupportingDocsEnhancedUpload: returning early because uploadFile returned a falsy value",
						{
							file,
							selectedDocument,
							documentContext,
							lastResponse,
							reason: "uploadFile returned falsy response",
						}
					);
					return null;
				}
			}

			supportingDocsLog(
				"[SupportingDocs] Calling API/Function: afterDocumentUpload",
				{
					documentContext,
					selectedDocument,
					selectedDocuments,
					lastResponse,
				}
			);
			const attached = await afterDocumentUpload?.({
				documentContext,
				selectedDocument,
				selectedDocuments,
				lastResponse,
			});
			supportingDocsLog(
				"[SupportingDocs] API/Function response: afterDocumentUpload",
				{
					documentContext,
					selectedDocument,
					selectedDocuments,
					lastResponse,
					attached,
				}
			);

			if (!attached) {
				supportingDocsLog(
					"runSupportingDocsEnhancedUpload: returning early because afterDocumentUpload returned a falsy value",
					{
						documentContext,
						selectedDocument,
						selectedDocuments,
						lastResponse,
						attached,
						reason: "afterDocumentUpload returned falsy response",
					}
				);
				return null;
			}

			supportingDocsLog(
				"runSupportingDocsEnhancedUpload: continuing to next selectedDocument",
				{
					selectedDocument,
					documentContext,
					attached,
				}
			);
		}

		try {
			supportingDocsLog("[SupportingDocs] Calling API/Function: onSuccess", {
				selectedDocuments,
			});
			await onSuccess?.();
			supportingDocsLog("[SupportingDocs] API/Function response: onSuccess", {
				selectedDocuments,
			});
		} catch (onSuccessError) {
			supportingDocsLog(
				"runSupportingDocsEnhancedUpload: onSuccess failed but flow will continue to closeDrawer and return true",
				{
					error: onSuccessError,
				}
			);
			console.error(
				"[SupportingDocs] enhanced upload post-success step failed",
				onSuccessError
			);
		}
		supportingDocsLog("[SupportingDocs] Closing drawer", { closeDrawer });
		closeDrawer?.();
		supportingDocsLog("runSupportingDocsEnhancedUpload: returning success", {
			returnValue: true,
			selectedDocuments,
		});
		supportingDocsLog("runSupportingDocsEnhancedUpload:end", {
			completed: true,
		});
		return true;
	} catch (err) {
		supportingDocsLog("runSupportingDocsEnhancedUpload: caught error", {
			error: err,
		});
		supportingDocsLog("runSupportingDocsEnhancedUpload:end", {
			completed: false,
		});
		await onError?.(err);
		supportingDocsLog("runSupportingDocsEnhancedUpload: returning null after error", {
			returnValue: null,
		});
		return null;
	}
};
